/**
 * The month calendar's grid (SE-6975, AC1).
 *
 * **Every date here is a CIVIL date, and none of it goes through local time.**
 * The API speaks `YYYY-MM-DD` at the venue, and the one-line way to handle that
 * — `new Date("2026-08-01").getDate()` — is wrong for half the planet: that
 * string parses as UTC midnight, and any viewer west of Greenwich reads it back
 * as the 31st of July. A calendar that puts a fixture on the wrong day is not a
 * cosmetic bug; it is the screen telling a scouting lead to be somewhere on the
 * wrong date.
 *
 * So the arithmetic is done on `{ year, month, day }` triples and on
 * `Date.UTC`, which has no offset to apply. Nothing in this module reads the
 * machine's clock or its timezone.
 * @module features/fixture-planning/utils/monthGrid
 */

/** A civil date, with no instant and no zone attached. */
export interface CivilDate {
  /** Full year, e.g. 2026. */
  readonly year: number;
  /** 1–12. NOT the zero-based month JavaScript's `Date` uses. */
  readonly month: number;
  /** 1–31. */
  readonly day: number;
}

/** One cell of the month grid. */
export interface MonthGridDay {
  /** The civil date this cell stands for. */
  readonly date: CivilDate;
  /** `YYYY-MM-DD`, the key fixtures are grouped by. */
  readonly iso: string;
  /** False for the leading and trailing days borrowed from adjacent months. */
  readonly isInMonth: boolean;
}

/** Days per week, named so the grid maths reads as calendar maths. */
const DAYS_IN_WEEK = 7;

/**
 * Pads a number to two digits.
 * @param value - The number.
 * @returns Two-digit string.
 */
const pad2 = (value: number): string => String(value).padStart(2, "0");

/**
 * Renders a civil date as `YYYY-MM-DD`.
 * @param date - The civil date.
 * @returns The ISO civil date.
 */
export const toIso = (date: CivilDate): string =>
  `${date.year}-${pad2(date.month)}-${pad2(date.day)}`;

/**
 * Parses `YYYY-MM-DD` into a civil date.
 *
 * Deliberately string surgery rather than `new Date(iso)`: the constructor
 * would hand back an instant that has to be read in SOME zone, and every choice
 * of zone is wrong for someone.
 * @param iso - A `YYYY-MM-DD` string.
 * @returns The civil date, or null when the string is not one.
 */
export const fromIso = (iso: string): CivilDate | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (match === null) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  // Rejects the 31st of February and friends: `Date.UTC` rolls them forward,
  // so a round-trip that changes the day proves the input was not a real date.
  const rolled = new Date(Date.UTC(year, month - 1, day));
  if (rolled.getUTCMonth() !== month - 1 || rolled.getUTCDate() !== day) {
    return null;
  }

  return { year, month, day };
};

/**
 * How many days a month has.
 * @param year - Full year.
 * @param month - 1–12.
 * @returns Day count, leap years included.
 */
export const daysInMonth = (year: number, month: number): number =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/**
 * The weekday of a civil date, Monday-first.
 *
 * Monday-first because the fixture list is a football week; a Sunday-first grid
 * splits a weekend across two rows.
 * @param date - The civil date.
 * @returns 0 for Monday through 6 for Sunday.
 */
export const weekdayIndex = (date: CivilDate): number => {
  const sundayFirst = new Date(
    Date.UTC(date.year, date.month - 1, date.day)
  ).getUTCDay();

  return (sundayFirst + 6) % DAYS_IN_WEEK;
};

/**
 * Steps a month, wrapping the year.
 * @param year - Full year.
 * @param month - 1–12.
 * @param delta - Months to add; negative goes back.
 * @returns The shifted year and month.
 */
export const shiftMonth = (
  year: number,
  month: number,
  delta: number
): { readonly year: number; readonly month: number } => {
  const zeroBased = month - 1 + delta;
  const shiftedYear = year + Math.floor(zeroBased / 12);
  // `%` keeps the sign of the dividend in JavaScript, so a December-to-January
  // step backwards would land on -1 without the second modulo.
  const shiftedMonth = ((zeroBased % 12) + 12) % 12;

  return { year: shiftedYear, month: shiftedMonth + 1 };
};

/**
 * Builds the month's grid, in whole weeks.
 *
 * Always whole weeks, so the grid never changes column count and a fixture on
 * the 1st does not sit alone on a short first row.
 * @param year - Full year.
 * @param month - 1–12.
 * @returns Weeks of seven days, Monday first.
 */
export const buildMonthGrid = (
  year: number,
  month: number
): readonly (readonly MonthGridDay[])[] => {
  const leading = weekdayIndex({ year, month, day: 1 });
  const length = daysInMonth(year, month);
  const previous = shiftMonth(year, month, -1);
  const next = shiftMonth(year, month, 1);
  const previousLength = daysInMonth(previous.year, previous.month);

  const cell = (date: CivilDate, isInMonth: boolean): MonthGridDay => ({
    date,
    iso: toIso(date),
    isInMonth,
  });

  const leadingDays = Array.from({ length: leading }, (_, index) =>
    cell(
      {
        year: previous.year,
        month: previous.month,
        day: previousLength - leading + index + 1,
      },
      false
    )
  );

  const monthDays = Array.from({ length }, (_, index) =>
    cell({ year, month, day: index + 1 }, true)
  );

  // Trailing days complete the final week only. A fixed six-row grid would add
  // an entire empty week to most months.
  const filled = leadingDays.length + monthDays.length;
  const remainder = filled % DAYS_IN_WEEK;
  const trailingDays = Array.from(
    { length: remainder === 0 ? 0 : DAYS_IN_WEEK - remainder },
    (_, index) =>
      cell({ year: next.year, month: next.month, day: index + 1 }, false)
  );

  const days = [...leadingDays, ...monthDays, ...trailingDays];

  return Array.from({ length: days.length / DAYS_IN_WEEK }, (_, week) =>
    days.slice(week * DAYS_IN_WEEK, week * DAYS_IN_WEEK + DAYS_IN_WEEK)
  );
};

/**
 * The inclusive civil-date window a month's grid covers.
 *
 * The API window has to match the GRID, not the month: the leading and trailing
 * cells are real days that render real fixtures, and querying only the month
 * would leave them permanently empty while looking perfectly loaded.
 * @param year - Full year.
 * @param month - 1–12.
 * @returns `fromDate` and `toDate`, both `YYYY-MM-DD` and inclusive.
 */
export const monthGridRange = (
  year: number,
  month: number
): { readonly fromDate: string; readonly toDate: string } => {
  const weeks = buildMonthGrid(year, month);
  const first = weeks[0][0];
  const lastWeek = weeks[weeks.length - 1];

  return {
    fromDate: first.iso,
    toDate: lastWeek[lastWeek.length - 1].iso,
  };
};
